import { createFileRoute, Link, Outlet } from "@tanstack/react-router";
import { createElement, useEffect } from "react";

import { SocketProvider, useSetupGlobalEvents } from "@/context/socket";
import { useSidebarMenu } from "@/hooks/use-sidebar-menu";
import { useAppStore } from "@/store/app";
import { cn } from "@/lib/utils";

import { AppHeader } from "@/components/dashboard/app-header";
import { ResponsiveMain } from "@/components/layout/main";
import { ProtectedLayout } from "@/components/layout/protected";
import { ResponsiveSidebar } from "@/components/layout/sidebar";
import { ResponsiveHeader } from "@/components/layout/header";
import { SettingSidebarInvoiceButton } from "@/components/dashboard/settings-sidebar";

export const Route = createFileRoute("/dashboard/settings/_layout")({
  component: DashboardLayout,
});

function DashboardLayout() {
  const setMode = useAppStore((state) => state.setSidebarMode);
  const [menu, { isActive }] = useSidebarMenu();

  useSetupGlobalEvents();

  useEffect(() => {
    setMode("collapsed");
    return () => setMode("expanded");
  }, [setMode]);

  return (
    <ProtectedLayout mode="dashboard">
      <SocketProvider>
        <ResponsiveSidebar settings />
        <ResponsiveMain settings>
          <ResponsiveHeader>
            <AppHeader />
          </ResponsiveHeader>
          <main className="pt-0 pb-6 lg:pt-8 lg:pb-8 flex-1 w-full overflow-y-auto lg:border-t">
            <div className="lg:hidden flex items-stretch justify-stretch gap-x-1.5 h-14 mb-8 overflow-x-auto scrollbar-hidden border-b sticky top-0 bg-background z-40">
              {menu
                .filter((group) => group.id === "settings")
                .map((group) => {
                  return group.children
                    .filter((item) => item.visible)
                    .map((item) => (
                      <Link
                        key={item.href}
                        to={item.href}
                        className={cn(
                          "inline-flex h-full w-full items-center justify-center gap-1.5 border-b px-3.5 py-1 text-sm tracking-wide font-medium whitespace-nowrap min-w-32",
                          isActive(item.href, item.exact) ? "border-primary text-foreground" : "border-transparent text-muted-foreground"
                        )}
                      >
                        {createElement(item.icon, { weight: "regular", className: "size-4 shrink-0" })}
                        <span className="leading-relaxed z-10">{item.label}</span>
                      </Link>
                    ));
                })}
              <SettingSidebarInvoiceButton className="items-center justify-center" />
            </div>
            <div className="w-full max-w-8xl mx-auto px-4 lg:px-10">
              <Outlet />
            </div>
          </main>
        </ResponsiveMain>
      </SocketProvider>
    </ProtectedLayout>
  );
}
